import {
  conceptInformationInterfaceWithNdcInformation,
  notesInterface,
  singleConceptInterface,
  singleContractInterface,
} from 'Interfaces/FDPInterfaces/tmsInterfaces';
import {
  invoiceSalesSummary,
  unconstrainedForecastInterfaceView,
} from 'Interfaces/FDPInterfaces/unconstrainedForecast';
import {
  UploadForecastCreatePayload,
  UploadForecastRow,
} from 'Interfaces/FDPInterfaces/unconstrainedForecastUpload';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { unconstrainedForecastService } from 'services/fdp/unconstrainedForecastService';
import { tmsScreenServices } from 'services/tmsScreenServices';
import { convertSalesPlanPlanningPeriodLabelArray } from 'utils/dateUtilities';
import { checkIfStringIsValidNdc } from 'utils/ndcUtilities';

export class UnconstrainedForecastViewStore {
  directcurrentNDCIndex: number = 0;
  indirectcurrentNDCIndex: number = 0;
  isAllConceptLoading: boolean = false;
  allDrugMoleculesDetailsForSearch: conceptInformationInterfaceWithNdcInformation[] = [];
  filteredDrugMoleculesDetails: conceptInformationInterfaceWithNdcInformation[] = [];
  conceptSearchInput: string = '';
  filteredConceptsForSearch: string[] = [];
  isConceptLoading: boolean = false;
  isConceptDetailsLoading: boolean = false;
  isunconstrainedForecastDataLoading: boolean = false;
  conceptDetails: singleConceptInterface | null = {
    concept: '',
    gfc: 0,
    generic_rxcui: '',
    therapeutic_category: '',
    pharmacologic_class: '',
    va_class_name: '',
    des_schedule: '',
    duration_condition: '',
    Strength: '',
  };
  contractDetails: singleContractInterface[] | [] = [
    {
      GFC: 0,
      ContractNumber: '',
      ContractType: '',
      StartDate: '',
      EndDate: '',
    },
  ];
  // NDCheading: { ndc: string; packSize: string }[] = [];
  directNDCheading: { ndc: string; packSize: string }[] = [];
  indirectNDCheading: { ndc: string; packSize: string }[] = [];
  unconstrainedForecastData: unconstrainedForecastInterfaceView = {
    GFC: 0,
    PlanningPeriod: '',
    forecasts: [
      {
        NDC11: '',
        PackSize: 0,
        SellingCategory: '',
        forecast_details: [
          {
            ID: 0,
            ForecastPeriod: '',
            StratCode: '',
            WholesalerDemandForecast: 0,
            Adjustment: 0,
            UnconstrainedForecast: 0,
            Notes: '',
            OldConstrainedForecast: null,
          },
        ],
      },
    ],
  };
  isBulkUpdateModalOpen: boolean = false;
  bulkNDCdata: {
    Status: boolean;
    Concept_info: {
      Concept: string;
      NDC11_list: {
        NDC11: string;
      }[];
    }[];
  }[] = [];

  bulkUpdateDirectTodoNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateIndirectTodoNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateDirectCompletedNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateIndirectCompletedNDC: {
    Concept: string;
    NDC11_list: {
      NDC11: string;
    }[];
  }[] = [];
  bulkUpdateNDCs: string[] = [];
  isForecastBulkUpdating: boolean = false;

  isInvoiceSalesLoading: boolean = false;
  invoiceSalesSummary: invoiceSalesSummary[] = [];

  isUnconstrainedForecastNotesDataLoading: boolean = false;
  UnconstrainedForecastNotesData: notesInterface[] = [];
  isUnconstrainedForecastProgressLoading: boolean = false;
  percentProgress: { percentage: number } = { percentage: 0 };
  unconstrainedForecastStatus: boolean = false;
  unconstrainedForecastPlanningPeriodLoading: boolean = false;
  unconstrainedForecastPlanningPeriods: { label: string; code: string }[] = [];
  lastNdcValueEntered: string = '';
  unconstrainedPeriod: { label: string; code: string } = {
    label: '',
    code: '',
  };
  forecastBtnLabel: string = 'New Forecast';
  UnconstrainedForecastStatusLoading: boolean = false;

  // ── Upload Excel state ──────────────────────────────────────────────────────
  uploadModalOpen: boolean = false;
  uploadValidationModalOpen: boolean = false;
  uploadRows: UploadForecastRow[] = [];
  uploadPlanningPeriod: string = '';
  isUploadingFile: boolean = false;
  isCreatingFromUpload: boolean = false;
  uploadFileError: string | null = null;
  uploadColumnErrors: string[] = [];

  // Timer handle for requestProgressRefresh's debounce - not app state, so it's
  // excluded from observable tracking.
  progressRefreshTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    makeAutoObservable(this, { progressRefreshTimer: false });
  }

  setDirectCurrentTabIndex = (newIndex: number) => {
    this.directcurrentNDCIndex = newIndex;
  };

  setIndirectCurrentTabIndex = (newIndex: number) => {
    this.indirectcurrentNDCIndex = newIndex;
  };

  setSearchInput = (value: string) => {
    this.conceptSearchInput = value;
  };

  // Filter function with custom debounce
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  handleFetchAllDetailsByGFC = async (gfc: string) => {
    this.isAllConceptLoading = true;
    try {
      this.fetchForecastProgress();
      const data = await tmsScreenServices.getAllActiveGSMSConceptsWithNdcInformation();
      this.fetchBulkUpdateNDCs();

      runInAction(() => {
        this.allDrugMoleculesDetailsForSearch = data;
        this.filteredDrugMoleculesDetails = data;
      });

      const [moleculeForGfcParam] = this.allDrugMoleculesDetailsForSearch.filter(
        drug => drug.GFC === parseInt(gfc)
      );
      if (moleculeForGfcParam) {
        this.conceptSearchInput = moleculeForGfcParam.Concept;
        this.handleSearch();
      } else {
        toast.error(`There is no molecule corresponding to gfc - ${gfc}`);
      }
    } catch (err) {
      console.error('Error while getting all drug details:', err);
    } finally {
      runInAction(() => {
        this.isAllConceptLoading = false;
      });
    }
  };

  handleFetchAllConceptDetailsForSearch = async () => {
    this.isAllConceptLoading = true;
    try {
      this.fetchForecastProgress();
      const data = await tmsScreenServices.getAllActiveGSMSConceptsWithNdcInformation();
      this.fetchBulkUpdateNDCs();

      runInAction(() => {
        this.allDrugMoleculesDetailsForSearch = data;
        this.filteredDrugMoleculesDetails = data;
      });
    } catch (err) {
      console.error('Error while getting all drug details:', err);
    } finally {
      runInAction(() => {
        this.isAllConceptLoading = false;
      });
    }
  };

  private debounce<T extends unknown[]>(func: (...args: T) => void, delay: number) {
    return (...args: T) => {
      if (this.debounceTimer) clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  handleFilterConceptSearch = this.debounce(() => {
    if (!this.conceptSearchInput.trim()) {
      this.filteredConceptsForSearch = [];
      return;
    }

    if (checkIfStringIsValidNdc(this.conceptSearchInput)) {
      const filteredNdc = this.allDrugMoleculesDetailsForSearch.filter(
        item => item.NDC11 === this.conceptSearchInput.trim()
      );
      if (filteredNdc.length === 1) {
        this.filteredConceptsForSearch = [`${filteredNdc[0].Concept} (NDC)`];
      }
      return;
    }

    const searchText = this.conceptSearchInput.toLowerCase();
    runInAction(() => {
      this.filteredConceptsForSearch = this.allDrugMoleculesDetailsForSearch
        .filter(concept => concept.Concept.toLowerCase().includes(searchText))
        .slice(0, 10)
        .map(concept => concept.Concept); // Extract only the `concept` string

      this.filteredConceptsForSearch = [...new Set(this.filteredConceptsForSearch)];

      // Auto-select logic could be added here if needed
    });
  }, 300);

  handleConceptSearchInputChange = (input: string) => {
    if (checkIfStringIsValidNdc(input)) {
      this.lastNdcValueEntered = input;
    }
    this.conceptSearchInput = input;
    this.handleFilterConceptSearch();
  };
  getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth()).padStart(2, '0'); // Months are zero-based
    return `${year}-${month}-01`;
  };

  handleGetConceptDetails = async (gfc_id: number) => {
    runInAction(() => {
      this.isConceptLoading = true;
      // this.isUnconstrainedForecastNotesDataLoading = true;
      this.isunconstrainedForecastDataLoading = true;
      this.unconstrainedForecastPlanningPeriodLoading = true;
      this.unconstrainedPeriod = { label: '', code: '' };
      this.unconstrainedForecastPlanningPeriods = [];
      // this.NDCheading = [];
      this.directNDCheading = [];
      this.indirectNDCheading = [];
      this.unconstrainedForecastData = {
        GFC: 0,
        PlanningPeriod: '',
        forecasts: [
          {
            NDC11: '',
            PackSize: 0,
            SellingCategory: '',
            forecast_details: [
              {
                ID: 0,
                ForecastPeriod: '',
                StratCode: '',
                WholesalerDemandForecast: 0,
                Adjustment: 0,
                UnconstrainedForecast: 0,
                Notes: '',
                OldConstrainedForecast: null,
              },
            ],
          },
        ],
      };
      this.UnconstrainedForecastNotesData = [];
    });
    try {
      // await this.fetchUnconstrainedForecastProgress(gfc_id);
      const data = await tmsScreenServices.getSingleGSMSConcept(gfc_id);
      runInAction(() => {
        this.conceptDetails = data.concept_info[0];
        this.contractDetails = data.contract_info;
      });
      this.fetchUnconstrainedPlanningPeriods(gfc_id);
      this.getInvoiceSalesSummary(gfc_id);
    } catch (error) {
      console.error('Error while getting concept details:', error);
    } finally {
      runInAction(() => {
        this.isConceptLoading = false;
      });
    }
  };

  handleSearch = async () => {
    if (!this.conceptSearchInput) return;

    const matchedMolecules = this.allDrugMoleculesDetailsForSearch.filter(
      concept => concept.Concept === this.conceptSearchInput
    );
    if (matchedMolecules.length === 0) {
      toast.error('No concept found with provided name');
    }

    const currURL = window.location.href;
    const url_check = currURL.includes('edit');
    if (url_check) {
      window.location.href = `/fdp/unconstrained_forecast?gfc=${matchedMolecules[0].GFC}`;
    }

    if (matchedMolecules.length > 0 && !url_check) {
      const selectedConcept = matchedMolecules[0];
      this.handleGetConceptDetails(selectedConcept.GFC);
    } else {
      this.isConceptDetailsLoading = false;
    }
  };

  handleEnterKeyDownConceptSearchField = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      this.handleSearch();
    }
  };

  setIsConceptDetailsLoading = (flag: boolean) => {
    this.isConceptDetailsLoading = flag;
  };

  fetchUnconstrainedPlanningPeriods = async (gfc: number) => {
    this.UnconstrainedForecastStatusLoading = true;
    try {
      const data = await unconstrainedForecastService.getUnconstrainedForecastPlanningPeriods(gfc);
      this.unconstrainedForecastPlanningPeriods = convertSalesPlanPlanningPeriodLabelArray(data.reverse());

      if (this.unconstrainedForecastPlanningPeriods.length > 0) {
        this.unconstrainedPeriod = this.unconstrainedForecastPlanningPeriods[0];
        await this.getUnconstrainedForecastViewData(String(gfc), this.unconstrainedPeriod.code);
        await this.fetchUnconstrainedForecastProgress(gfc);
      } else {
        this.isunconstrainedForecastDataLoading = false;
        this.UnconstrainedForecastStatusLoading = false;
        this.forecastBtnLabel = 'New Forecast';
        runInAction(() => {
          this.unconstrainedForecastData = {
            GFC: 0,
            PlanningPeriod: '',
            forecasts: [
              {
                NDC11: '',
                PackSize: 0,
                SellingCategory: '',
                forecast_details: [
                  {
                    ID: 0,
                    ForecastPeriod: '',
                    StratCode: '',
                    WholesalerDemandForecast: 0,
                    Adjustment: 0,
                    UnconstrainedForecast: 0,
                    Notes: '',
                    OldConstrainedForecast: null,
                  },
                ],
              },
            ],
          };
        });
      }
    } catch (error) {
      console.log(
        'UnconstrainedForecastViewScreenStore fetchUnconstrainedPlanningPeriods => ',
        error
      );
    } finally {
      this.unconstrainedForecastPlanningPeriodLoading = false;
    }
  };

  setUnconstrainedForecastPeriod = (option: { label: string; code: string }) => {
    this.unconstrainedPeriod = option;
    if (this.conceptDetails?.gfc) {
      this.getUnconstrainedForecastViewData(String(this.conceptDetails?.gfc), option.code);
    }
  };

  fetchUnconstrainedForecastProgress = async (gfc: number) => {
    this.UnconstrainedForecastStatusLoading = true;
    try {
      const data = await unconstrainedForecastService.getUnconstrainedForecastStatus(gfc);
      if (data && data.length > 0) {
        let unconstrainedStatus: boolean = true;
        let matchedNDCCount: number = 0;
        const directNDCSet = new Set(this.directNDCheading.map(item => item.ndc));
        const indirectNDCSet = new Set(this.indirectNDCheading.map(item => item.ndc));

        for (const forecast of data) {
          if (!forecast.UnconstrainedForecastStatus) {
            unconstrainedStatus = false;
          }
          if (directNDCSet.has(forecast.NDC11) || indirectNDCSet.has(forecast.NDC11)) {
            matchedNDCCount++;
          }
        }

        const allMatched = matchedNDCCount === data.length;
        if (allMatched && !unconstrainedStatus) {
          this.forecastBtnLabel = 'New Forecast';
        } else if (allMatched && unconstrainedStatus) {
          this.forecastBtnLabel = 'Edit Forecast';
        } else if (!allMatched && !unconstrainedStatus) {
          this.forecastBtnLabel = 'Continue Forecast';
        }
        this.unconstrainedForecastStatus = unconstrainedStatus;
      }
    } catch (error) {
      console.log('UnconstrainedForecastViewScreenStore fetchForecastProgress => ', error);
    } finally {
      this.UnconstrainedForecastStatusLoading = false;
    }
  };

  fetchForecastProgress = async () => {
    this.isUnconstrainedForecastProgressLoading = true;
    try {
      const data = await unconstrainedForecastService.getUnconstrainedForecastProgress();
      this.percentProgress = data;
    } catch (error) {
      console.log('UnconstrainedForecastViewScreenStore fetchForecastProgress => ', error);
    } finally {
      // this.isUnconstrainedForecastProgressLoading = false;
    }
  };

  fetchForecastGFCNotes = async (ndc11: string, signal?: AbortSignal) => {
    if (!ndc11) return;

    this.isUnconstrainedForecastNotesDataLoading = true;
    try {
      this.UnconstrainedForecastNotesData = await unconstrainedForecastService.getNDC11Note(
        ndc11,
        'unconstrained',
        signal
      );
    } catch (error) {
      console.log('Unconstrained Forecast view store fetchMSGFCNotes => ', error);
    } finally {
      this.isUnconstrainedForecastNotesDataLoading = false;
    }
  };

  saveForecastNote = async (gfc: number, note: string, planning_type: string) => {
    let data: notesInterface[] = [];
    try {
      const payload = {
        gfc: gfc,
        note: note,
        planning_type: planning_type,
      };
      data = await tmsScreenServices.saveGFCNote(payload);
    } catch (error) {
      console.log('Error saving Note=> ', error);
    } finally {
      this.UnconstrainedForecastNotesData = data;
    }
  };

  getInvoiceSalesSummary = async (gfc: number) => {
    this.isInvoiceSalesLoading = true;
    try {
      const response = await unconstrainedForecastService.getSalesSummaryDetails(gfc);
      this.invoiceSalesSummary = response;
    } catch (error) {
      console.log('getInvoiceSalesSummary=> ', error);
    } finally {
      this.isInvoiceSalesLoading = false;
    }
  };

  getUnconstrainedForecastViewData = async (gfc: string, planning_period: string) => {
    this.isunconstrainedForecastDataLoading = true;
    try {
      const response = await unconstrainedForecastService.getUnconstrainedForecastView(
        gfc,
        planning_period
      );
      runInAction(() => {
        this.unconstrainedForecastData = response;
      });
      this.fetchNDCHeadings();
    } catch (error) {
      console.log('getUnconstrainedForecastViewData=> ', error);
    } finally {
      this.isunconstrainedForecastDataLoading = false;
    }
  };

  fetchBulkUpdateNDCs = async () => {
    try {
      const data = await unconstrainedForecastService.getBulkNDCList();

      this.bulkUpdateDirectTodoNDC = data.plan_status.Direct.ToDo;
      this.bulkUpdateDirectCompletedNDC = data.plan_status.Direct.Completed;
      this.bulkUpdateIndirectTodoNDC = data.plan_status.Indirect.ToDo;
      this.bulkUpdateIndirectCompletedNDC = data.plan_status.Indirect.Completed;
    } catch (error) {
      console.log('fetchBulkUpdateNDCs=> ', error);
    } finally {
      this.isUnconstrainedForecastProgressLoading = false;
    }
  };

  // Debounced entry point for "refresh progress after a save". Every save path
  // (single-edit full/category/single-NDC save, bulk-edit single/save-all) calls
  // this instead of fetching directly. Saving several bulk-edit rows one after
  // another re-arms the same timer each time, so only ONE fetchForecastProgress
  // + fetchBulkUpdateNDCs pair actually fires, shortly after the last save
  // settles - not one pair per row saved.
  requestProgressRefresh = (delayMs: number = 800) => {
    if (this.progressRefreshTimer) {
      clearTimeout(this.progressRefreshTimer);
    }
    this.progressRefreshTimer = setTimeout(() => {
      this.progressRefreshTimer = null;
      this.fetchForecastProgress();
      this.fetchBulkUpdateNDCs();
    }, delayMs);
  };

  fetchNDCHeadings = () => {
    // filtering out all the NDC for direct selling categories
    this.directNDCheading = [];
    this.indirectNDCheading = [];
    for (const forecast of this.unconstrainedForecastData.forecasts) {
      if (
        forecast.SellingCategory.toLocaleLowerCase() === 'direct' &&
        this.directNDCheading.filter(item => item.ndc === forecast.NDC11).length === 0
      ) {
        this.directNDCheading.push({
          ndc: forecast.NDC11,
          packSize: String(forecast.PackSize),
        });
      } else if (
        forecast.SellingCategory.toLocaleLowerCase() === 'indirect' &&
        !this.indirectNDCheading.some(item => item.ndc === forecast.NDC11)
      ) {
        this.indirectNDCheading.push({
          ndc: forecast.NDC11,
          packSize: String(forecast.PackSize),
        });
      }
    }
    if (this.directNDCheading[0]) {
      this.fetchForecastGFCNotes(this.directNDCheading[0]?.ndc);
    }
  };

  bulkUpdateUnconstrainedForecast = async (selling_category: 'direct' | 'indirect' | 'all') => {
    try {
      this.isForecastBulkUpdating = true;
      await unconstrainedForecastService.bulkUpdateNDCs({ selling_category });
    } catch (error) {
      console.log('bulkUpdateUnconstrainedForecast', error);
    } finally {
      this.fetchBulkUpdateNDCs();
      this.fetchForecastProgress();
      this.isForecastBulkUpdating = false;
      this.isBulkUpdateModalOpen = false;
    }
  };

  // Opening the modal does NOT refetch - progress/bulk-list data only refreshes
  // on screen load and after an actual unconstrained forecast save
  // (requestProgressRefresh), never just from clicking the percentage badge.
  toggleBulkUpdateModal = () => {
    this.isBulkUpdateModalOpen = !this.isBulkUpdateModalOpen;
  };

  // ── Upload Excel actions ────────────────────────────────────────────────────

  toggleUploadModal = () => {
    this.uploadModalOpen = !this.uploadModalOpen;
    if (!this.uploadModalOpen) {
      this.uploadFileError = null;
      this.uploadColumnErrors = [];
    }
  };

  toggleUploadValidationModal = () => {
    this.uploadValidationModalOpen = !this.uploadValidationModalOpen;
  };

  updateUploadRowNote = (rowIndex: number, note: string) => {
    this.uploadRows[rowIndex].note = note;
  };

  deleteUploadRow = (rowIndex: number) => {
    this.uploadRows.splice(rowIndex, 1);
  };

  handleUploadFile = async (file: File) => {
    try {
      this.isUploadingFile = true;
      this.uploadFileError = null;
      this.uploadColumnErrors = [];
      const formData = new FormData();
      formData.append('file', file);
      const response = await unconstrainedForecastService.uploadForecastPreview(formData);
      runInAction(() => {
        this.uploadRows = response.rows.map(row => ({ ...row, note: '' }));
        this.uploadPlanningPeriod = response.planning_period;
        this.uploadModalOpen = false;
        this.uploadValidationModalOpen = true;
      });
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { error?: string; column_errors?: string[] } };
      };
      const message =
        axiosErr?.response?.data?.error ||
        'The uploaded file could not be processed. Please re-download the template and try again.';
      const columnErrors = axiosErr?.response?.data?.column_errors ?? [];
      runInAction(() => {
        this.uploadFileError = message;
        this.uploadColumnErrors = columnErrors;
      });
    } finally {
      runInAction(() => {
        this.isUploadingFile = false;
      });
    }
  };

  handleCreateFromUpload = async () => {
    try {
      this.isCreatingFromUpload = true;
      const payload: UploadForecastCreatePayload = {
        planning_period: this.uploadPlanningPeriod,
        gfc: this.unconstrainedForecastData.GFC || undefined,
        rows: this.uploadRows.map(r => ({
          ndc11: r.ndc11,
          selling_category: r.selling_category,
          status: r.status,
          note: r.note,
          forecast_data: r.forecast_data,
        })),
      };
      await unconstrainedForecastService.createForecastFromUpload(payload);
      toast.success('Unconstrained forecast created successfully.');
      runInAction(() => {
        this.uploadValidationModalOpen = false;
        this.uploadRows = [];
        this.uploadPlanningPeriod = '';
      });
      // Refresh the current forecast view if a concept is already loaded
      if (this.unconstrainedForecastData?.GFC && this.unconstrainedPeriod?.code) {
        await this.getUnconstrainedForecastViewData(
          String(this.unconstrainedForecastData.GFC),
          this.unconstrainedPeriod.code
        );
      }
    } catch {
      toast.error('Failed to create unconstrained forecast. Please try again.');
    } finally {
      runInAction(() => {
        this.isCreatingFromUpload = false;
      });
    }
  };

  resetStore = () => {
    // Tab and layout state
    this.directcurrentNDCIndex = 0;
    this.indirectcurrentNDCIndex = 0;

    // Loading states
    this.isAllConceptLoading = false;
    this.isConceptLoading = false;
    this.isConceptDetailsLoading = false;
    this.isunconstrainedForecastDataLoading = false;
    this.isForecastBulkUpdating = false;
    this.isInvoiceSalesLoading = false;
    this.isUnconstrainedForecastNotesDataLoading = false;
    this.isUnconstrainedForecastProgressLoading = false;
    this.unconstrainedForecastPlanningPeriodLoading = false;
    this.UnconstrainedForecastStatusLoading = false;

    // Search related data
    this.allDrugMoleculesDetailsForSearch = [];
    this.filteredDrugMoleculesDetails = [];
    this.conceptSearchInput = '';
    this.filteredConceptsForSearch = [];

    // Concept and contract details
    this.conceptDetails = {
      concept: '',
      gfc: 0,
      generic_rxcui: '',
      therapeutic_category: '',
      pharmacologic_class: '',
      va_class_name: '',
      des_schedule: '',
      duration_condition: '',
      Strength: '',
    };
    this.contractDetails = [
      {
        GFC: 0,
        ContractNumber: '',
        ContractType: '',
        StartDate: '',
        EndDate: '',
      },
    ];

    // NDC data
    this.directNDCheading = [];
    this.indirectNDCheading = [];
    this.lastNdcValueEntered = '';

    // Forecast data
    this.unconstrainedForecastData = {
      GFC: 0,
      PlanningPeriod: '',
      forecasts: [
        {
          NDC11: '',
          PackSize: 0,
          SellingCategory: '',
          forecast_details: [
            {
              ID: 0,
              ForecastPeriod: '',
              StratCode: '',
              WholesalerDemandForecast: 0,
              Adjustment: 0,
              UnconstrainedForecast: 0,
              Notes: '',
              OldConstrainedForecast: null,
            },
          ],
        },
      ],
    };

    // Bulk update data
    this.isBulkUpdateModalOpen = false;
    this.bulkUpdateDirectTodoNDC = [];
    this.bulkUpdateIndirectTodoNDC = [];
    this.bulkUpdateDirectCompletedNDC = [];
    this.bulkUpdateIndirectCompletedNDC = [];
    this.bulkUpdateNDCs = [];

    // Progress and notes data
    this.invoiceSalesSummary = [];
    this.UnconstrainedForecastNotesData = [];
    this.percentProgress = { percentage: 0 };
    this.unconstrainedForecastStatus = false;
    this.unconstrainedForecastPlanningPeriods = [];
    this.unconstrainedPeriod = { label: '', code: '' };
    this.forecastBtnLabel = 'New Forecast';

    // Upload Excel state
    this.uploadModalOpen = false;
    this.uploadValidationModalOpen = false;
    this.uploadRows = [];
    this.uploadPlanningPeriod = '';
    this.isUploadingFile = false;
    this.isCreatingFromUpload = false;
    this.uploadFileError = null;
    this.uploadColumnErrors = [];
  };
}
